import React, { useMemo, useState } from "react";
import { Avatar, Button, Input, Typography } from "antd";
import { CloseOutlined, UserOutlined } from "@ant-design/icons";
import { useChat } from "./ChatContext";
import { resolveAvatarUrl } from "./zulipClient";
import type { PresenceStatus, ZulipUser } from "./types";

const STATUS_RANK: Record<PresenceStatus, number> = { active: 0, idle: 1, offline: 2 };

/**
 * The Google Chat-style "people" panel — a flat, presence-sorted, searchable
 * roster of the realm, collapsed by default (see ChatPanel). Clicking anyone
 * opens/starts a DM with them, reusing the same selectDm the conversation
 * list uses. `onClose` always collapses/hides this panel (the header's own
 * explicit return button — see ChatPanel, where it's wired to the same
 * toggle the People header icon uses); `onPersonSelect` is additionally
 * passed in widget mode, where this panel shares its pane with
 * ThreadView/ConversationList (see ChatPanel), so picking a person also
 * switches that shared pane back to the thread.
 */
const PeopleList: React.FC<{ onClose: () => void; onPersonSelect?: () => void }> = ({ onClose, onPersonSelect }) => {
  const { realmUsers, myUserId, selectDm, presenceStatusFor, roleFor } = useChat();
  const [search, setSearch] = useState("");

  const sorted = useMemo(() => {
    const rank = (u: ZulipUser) => (u.user_id === myUserId ? -1 : STATUS_RANK[presenceStatusFor(u.email)]);
    const q = search.trim().toLowerCase();
    return [...realmUsers]
      .filter((u) => u.is_active !== false)
      .filter((u) => !q || u.full_name.toLowerCase().includes(q))
      .sort((a, b) => rank(a) - rank(b) || a.full_name.localeCompare(b.full_name));
  }, [realmUsers, myUserId, presenceStatusFor, search]);

  return (
    <div className="chat-people-list">
      <div className="chat-people-list__header">
        <span>People</span>
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />
      </div>
      <div className="chat-people-list__search">
        <Input.Search placeholder="Search people" allowClear size="small" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="chat-people-list__scroll">
        {sorted.length === 0 && (
          <Typography.Text type="secondary" className="chat-conversation-list__empty-hint">
            No one matches &quot;{search}&quot;
          </Typography.Text>
        )}
        {sorted.map((u) => {
          const isMe = u.user_id === myUserId;
          const status = isMe ? "active" : presenceStatusFor(u.email);
          const role = roleFor(u.email);
          return (
            <div
              key={u.user_id}
              className={`chat-people-row${isMe ? " chat-people-row--me" : ""}`}
              onClick={() => {
                if (isMe) return;
                selectDm([u.user_id], u.full_name);
                onPersonSelect?.();
              }}
            >
              <span className="chat-people-row__avatar-wrap">
                <Avatar size={28} src={resolveAvatarUrl(u.avatar_url) || undefined} icon={<UserOutlined />}>
                  {!u.avatar_url && u.full_name.charAt(0).toUpperCase()}
                </Avatar>
                <span className={`chat-presence-dot chat-presence-dot--${status}`} />
              </span>
              <div className="chat-people-row__main">
                <Typography.Text ellipsis className="chat-people-row__name">
                  {u.full_name}
                  {isMe && " (you)"}
                </Typography.Text>
                {role && <span className="chat-people-row__role">{role}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PeopleList;
